import { Component, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { IconsModule, KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { AbstractGridComponent } from '@unity/abstract/abstract-grid.component';
import { ReferenceFileDetailedDto, ReferenceFilesClient } from '@unity/clients';
import { TypedDirective } from '@unity/directives/typed.directive';
import { DownloadService } from '@unity/services/download.service';
import { GridSharedComponent } from '@unity/shared/composed/grid-shared/grid-shared.component';
import { ButtonDownloadSharedComponent } from '@unity/shared/presentation/button-download-shared/button-download-shared.component';
import { LinkGridcellSharedComponent } from '@unity/shared/presentation/link-gridcell-shared/link-gridcell-shared.component';
import { SortConfig } from '@unity/utils/types';
import { take, tap } from 'rxjs';

@Component({
  selector: 'unity-grid-reference-file-shared',
  standalone: true,
  imports: [
    KENDO_GRID,
    KENDO_SVGICON,
    KENDO_BUTTON,
    IconsModule,
    GridSharedComponent,
    LinkGridcellSharedComponent,
    ButtonDownloadSharedComponent,
    TypedDirective,
  ],
  templateUrl: './grid-reference-file-shared.component.html',
  styleUrl: './grid-reference-file-shared.component.scss',
})
export class GridReferenceFileSharedComponent extends AbstractGridComponent<ReferenceFileDetailedDto> {
  private readonly referenceFilesClient = inject(ReferenceFilesClient);
  private readonly downloadService = inject(DownloadService);

  protected override readonly resource = rxResource({
    params: () => {
      const query = this.$gridifyQuery();
      const agreementId = this.$agreementId();
      return query && agreementId ? { query, agreementId } : undefined;
    },
    stream: ({ params: { agreementId, query } }) =>
      this.referenceFilesClient.getAllForAgreementAsDetailed(agreementId, query.page, query.pageSize, query.orderBy, query.filter),
  });

  protected defaultSort: SortConfig[] = [
    {
      field: 'created_dt',
      dir: 'desc',
    },
  ];
  protected readonly $activeDownloadIdSet = signal<Set<number>>(new Set());

  public readonly $agreementId = input.required<number>({ alias: 'agreementId' });

  protected downloadFile(referenceFile: ReferenceFileDetailedDto) {
    this.$activeDownloadIdSet.update(prev => prev.add(referenceFile.id));
    this.referenceFilesClient
      .downloadOneForAgreement(referenceFile.agreement.id, referenceFile.id)
      .pipe(
        take(1),
        tap(res => this.downloadService.saveFile(res))
      )
      .subscribe({
        complete: () =>
          this.$activeDownloadIdSet.update(prev => {
            prev.delete(referenceFile.id);
            return prev;
          }),
      });
  }
}
